/**
 * One write path for version history and wording overlays across surfaces.
 */

import type { QuestionFormValues } from "@/components/question-composer/composer-form"
import {
  leoSpottedOverlayKeys,
  overlayForLibraryBankRow,
  patchLibraryBankRowOverlay,
  type LibraryBankRowOverlay,
} from "@/lib/library-bank-row-overlays"
import type { AssessmentBankQuestion } from "@/lib/mock/assessment-add-questions"
import { questionCatalogById } from "@/lib/mock/question-catalog"
import { writeAssessmentQuestionBankRow } from "@/lib/question-version-history/assessment-session"
import { trackEdited, trackStatusChange } from "@/lib/objects/track"
import { ensureInBank } from "@/lib/question-bank/bank-store"
import { toQuestionStatus } from "@/lib/question-status"
import { writeComposerFormDraft } from "@/lib/question-version-history/composer-draft"

export type QuestionHistorySurface =
  | "library-bank"
  | "assessment-paper"
  | "assessment-compose"
  | "add-questions"

export type QuestionHistoryWriteInput = {
  /** Library overlay fields (stem, options, revisions, …). */
  overlay?: LibraryBankRowOverlay
  /** Full bank row for assessment preview and compose history parity. */
  assessmentBankRow?: AssessmentBankQuestion
  /** Unsaved composer form snapshot. */
  composerForm?: QuestionFormValues
}

export type QuestionHistoryWriteContext = {
  surface: QuestionHistorySurface
  assessmentId?: string
  /** Extra ids for the same question (catalog, bank, paper code). */
  alsoIds?: string[]
}

/**
 * Persist session state for a question so refresh and surface hops stay aligned.
 *
 * New surfaces should call this instead of patching storage ad hoc.
 */
export function persistQuestionHistory(
  questionId: string,
  input: QuestionHistoryWriteInput,
  ctx: QuestionHistoryWriteContext,
) {
  if (!questionId) return

  const alsoIds = [
    ...new Set([
      ...(ctx.alsoIds ?? []),
      ...leoSpottedOverlayKeys(questionId).filter((key) => key !== questionId),
    ]),
  ]

  // One history for the question, whichever surface saved it.
  const before = overlayForLibraryBankRow(questionId)
  const nextStatus = input.overlay?.status ?? input.assessmentBankRow?.status
  if (nextStatus !== undefined && toQuestionStatus(nextStatus) !== toQuestionStatus(before?.status)) {
    trackStatusChange("question", questionId, toQuestionStatus(before?.status), toQuestionStatus(nextStatus))
  } else if (input.overlay || input.assessmentBankRow || input.composerForm) {
    trackEdited("question", questionId, "Edited")
  }

  if (input.overlay) {
    patchLibraryBankRowOverlay(questionId, input.overlay, alsoIds)
  } else if (input.assessmentBankRow?.revisions?.length) {
    patchLibraryBankRowOverlay(
      questionId,
      { revisions: input.assessmentBankRow.revisions },
      alsoIds,
    )
  }

  if (input.assessmentBankRow && ctx.assessmentId) {
    writeAssessmentQuestionBankRow(ctx.assessmentId, {
      ...input.assessmentBankRow,
      id: questionId,
    })
    // A question made or edited inside an assessment belongs in the bank too.
    ensureInBank([{ ...input.assessmentBankRow, id: questionId }])
  }

  if (input.composerForm) {
    writeComposerFormDraft(questionId, input.composerForm)
  }

  void ctx.surface
}

/** Session overlay on a bank row so preview and compose show the same wording. */
export function mergeOverlayIntoBankQuestion(
  row: AssessmentBankQuestion,
): AssessmentBankQuestion {
  const catalog = questionCatalogById(row.id)
  const overlay =
    overlayForLibraryBankRow(row.id, catalog?.questionId) ??
    overlayForLibraryBankRow(catalog?.id ?? "", catalog?.questionId) ??
    overlayForLibraryBankRow(
      catalog ? `ai-case-${catalog.id}` : row.id,
      catalog?.questionId,
    )
  if (!overlay) return row
  return {
    ...row,
    ...(overlay.stem ? { stem: overlay.stem } : {}),
    ...(overlay.title ? { title: overlay.title } : {}),
    ...(overlay.rationale ? { rationale: overlay.rationale } : {}),
    ...(overlay.options?.length
      ? {
          carriedChoices: overlay.options.map((option) => ({
            text: option.text,
            isCorrect: option.isCorrect,
          })),
        }
      : {}),
    ...(overlay.revisions?.length ? { revisions: overlay.revisions } : {}),
    // Classification is part of what was saved, not only the wording. A row
    // that kept its old Bloom's level or status after the author changed it
    // is the same stale-copy bug, one field over.
    ...(overlay.bloomLevel
      ? { bloom: overlay.bloomLevel as AssessmentBankQuestion["bloom"] }
      : {}),
    ...(overlay.difficulty
      ? { difficulty: BANK_DIFFICULTY[overlay.difficulty] ?? row.difficulty }
      : {}),
    ...(overlay.status ? { status: toQuestionStatus(overlay.status) } : {}),
    ...(overlay.categoryIds?.length ? { categoryIds: overlay.categoryIds } : {}),
    ...(overlay.classifications ? { classifications: overlay.classifications } : {}),
  }
}

const BANK_DIFFICULTY: Record<string, AssessmentBankQuestion["difficulty"]> = {
  easy: "Easy",
  medium: "Medium",
  hard: "Hard",
}

const OVERLAY_DIFFICULTY: Record<string, LibraryBankRowOverlay["difficulty"]> = {
  Easy: "easy",
  Medium: "medium",
  Hard: "hard",
}

/** Overlay patch from a saved or Leo-updated bank row. */
export function overlayPatchFromAssessmentBankRow(
  row: AssessmentBankQuestion,
): LibraryBankRowOverlay {
  return {
    stem: row.stem,
    title: row.title,
    rationale: row.rationale,
    status: toQuestionStatus(row.status),
    ...(row.bloom ? { bloomLevel: row.bloom } : {}),
    ...(row.difficultyTagged !== false && OVERLAY_DIFFICULTY[row.difficulty]
      ? { difficulty: OVERLAY_DIFFICULTY[row.difficulty] }
      : {}),
    ...(row.categoryIds?.length ? { categoryIds: row.categoryIds } : {}),
    ...(row.revisions?.length ? { revisions: row.revisions } : {}),
    ...(row.carriedChoices?.length
      ? {
          options: row.carriedChoices.map((choice) => ({
            text: choice.text,
            isCorrect: choice.isCorrect,
          })),
        }
      : {}),
  }
}
